import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, Link } from "react-router-dom";
import DashLayout from "@/components/DashLayout";
import SignaturePad from "@/components/SignaturePad";
import BodyMap from "@/components/BodyMap";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import {
  ClipboardCheck, ChevronDown, ChevronRight, Plus, Copy, Search, ArrowLeft, Lock, Unlock,
  Pencil, ShieldCheck, ScrollText, FileSignature, History, AlertTriangle, CheckCircle2,
  User, Stethoscope, Ruler, Activity, Target, HeartHandshake, ClipboardList, FilePlus,
  BarChart3, BookOpen, MapPin,
} from "lucide-react";

/* =========================================================================
   Static clinical vocabulary
   ========================================================================= */
const ASSESSMENT_TYPES = [
  { value: "initial", label: "Initial assessment" },
  { value: "follow_up", label: "Follow-up" },
  { value: "reassessment", label: "Reassessment" },
  { value: "discharge", label: "Discharge" },
  { value: "remote_consultation", label: "Remote consultation" },
  { value: "sports_massage", label: "Sports massage" },
  { value: "sc_assessment", label: "Strength & conditioning" },
  { value: "post_op", label: "Post-operative" },
  { value: "running_assessment", label: "Running assessment" },
  { value: "return_to_sport", label: "Return-to-sport" },
];

const NOTE_STATUS_COLOURS = {
  draft: { bg: "#3B82F6", fg: "#93C5FD", label: "Draft" },
  completed: { bg: "#F59E0B", fg: "#FCD34D", label: "Completed" },
  signed: { bg: "#22C55E", fg: "#86EFAC", label: "Signed" },
  locked: { bg: "#64748B", fg: "#E2E8F0", label: "Locked" },
  corrected: { bg: "#8B5CF6", fg: "#C4B5FD", label: "Addendum" },
  cancelled: { bg: "#6B7280", fg: "#D1D5DB", label: "Cancelled" },
};

const SERVICES = [
  "Initial Assessment", "Follow-Up", "Sports Massage", "S&C session",
  "Return-to-sport testing", "Running Analysis", "Post-op rehabilitation",
  "Remote consultation", "Home visit",
];

const BODY_AREAS = [
  "Cervical spine", "Thoracic spine", "Lumbar spine", "Shoulder", "Elbow", "Wrist/Hand",
  "Hip/Pelvis", "Knee", "Ankle/Foot", "Multiple regions", "Whole body",
];

/* =========================================================================
   Small UI primitives
   ========================================================================= */
function StatusBadge({ status }) {
  const { t } = useTranslation();
  const c = NOTE_STATUS_COLOURS[status] || NOTE_STATUS_COLOURS.draft;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ background: `${c.bg}22`, color: c.fg || c.bg, border: `1px solid ${c.bg}66` }}
      data-testid={`status-${status}`}
    >
      {status === "locked" && <Lock size={10} />} {t(`assess.status.${status}`, c.label)}
    </span>
  );
}

function Field({ label, hint, required, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="text-xs mb-1 flex items-center gap-1" style={{ color: "var(--fl-muted)" }}>
        <span>{label}</span>
        {required && <span style={{ color: "var(--fl-orange)" }}>*</span>}
        {hint && <span style={{ color: "var(--fl-muted-2)" }}>· {hint}</span>}
      </div>
      {children}
    </label>
  );
}

function Section({ title, icon: Icon, id, defaultOpen = false, required, children, testid }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section id={id} className="fl-card p-4 md:p-5" data-testid={testid || `section-${id}`}>
      <button
        type="button"
        className="w-full flex items-center justify-between text-left"
        onClick={() => setOpen((v) => !v)}
        data-testid={`toggle-${id}`}
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon size={18} className="text-[color:var(--fl-orange)]" />}
          <div>
            <div className="fl-heading text-base">{title}</div>
            {required && <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--fl-orange)" }}>{t("assess.required")}</div>}
          </div>
        </div>
        {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
      </button>
      {open && <div className="mt-4 space-y-4">{children}</div>}
    </section>
  );
}

function Input({ value, onChange, placeholder, type = "text", locked, testid, ...rest }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
      placeholder={placeholder}
      disabled={locked}
      className="fl-input w-full text-sm px-3 py-2 rounded-md disabled:opacity-60"
      data-testid={testid}
      {...rest}
    />
  );
}

function Textarea({ value, onChange, placeholder, rows = 3, locked, testid }) {
  return (
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      disabled={locked}
      className="fl-input w-full text-sm px-3 py-2 rounded-md disabled:opacity-60"
      data-testid={testid}
    />
  );
}

function Select({ value, onChange, options, locked, testid }) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={locked}
      className="fl-input w-full text-sm px-3 py-2 rounded-md disabled:opacity-60"
      data-testid={testid}
    >
      <option value="">—</option>
      {options.map((o) => (
        <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>
          {typeof o === "string" ? o : o.label}
        </option>
      ))}
    </select>
  );
}

function ChoiceRow({ value, onChange, options = ["Yes", "No", "N/A", "Not assessed", "No concerns"], locked, testid }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o}
          type="button"
          disabled={locked}
          onClick={() => onChange(value === o ? "" : o)}
          className="px-2 py-1 text-xs rounded-full border transition-all disabled:opacity-50"
          style={{
            background: value === o ? "var(--fl-orange)" : "transparent",
            color: value === o ? "#111" : "var(--fl-muted)",
            borderColor: value === o ? "var(--fl-orange)" : "var(--fl-border)",
          }}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

/* =========================================================================
   ROM Table
   ========================================================================= */
function ROMTable({ rom, setRom, refs, locked }) {
  const { t } = useTranslation();
  const [region, setRegion] = useState("shoulder");
  const regionMovements = refs?.regions?.[region] || [];

  const addRow = (movement, typical) => {
    setRom([...(rom || []), {
      id: crypto.randomUUID(),
      region,
      movement,
      side: "R",
      arom: "",
      prom: "",
      typical_deg: typical,
      pain: "",
      end_feel: "",
      method: "",
      position: "",
      quality: "",
      compensation: "",
      previous: "",
      baseline: "",
      notes: "",
    }]);
  };

  const update = (id, patch) => setRom((rom || []).map((r) => r.id === id ? { ...r, ...patch } : r));
  const remove = (id) => setRom((rom || []).filter((r) => r.id !== id));

  const colorForDelta = (row) => {
    const a = Number(row.arom), t = Number(row.typical_deg);
    if (!a || !t) return "var(--fl-text)";
    if (a >= t * 0.95) return "#22C55E";
    if (a >= t * 0.7) return "var(--fl-text)";
    return "#F59E0B";
  };

  return (
    <div className="space-y-3">
      <div className="text-xs p-3 rounded-md" style={{ background: "color-mix(in oklab, var(--fl-orange) 8%, transparent)", color: "var(--fl-muted)", border: "1px dashed color-mix(in oklab, var(--fl-orange) 40%, var(--fl-border))" }}>
        {refs?.disclaimer}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs" style={{ color: "var(--fl-muted)" }}>{t("assess.romRegion")}:</span>
        <select
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          className="fl-input text-xs px-2 py-1 rounded-md"
          disabled={locked}
          data-testid="rom-region-select"
        >
          {Object.keys(refs?.regions || {}).map((k) => (
            <option key={k} value={k}>{k.replace(/_/g, " ")}</option>
          ))}
        </select>
        <div className="flex flex-wrap gap-1 ml-2">
          {regionMovements.map((m) => (
            <button
              key={m.movement}
              type="button"
              onClick={() => addRow(m.movement, m.typical_deg)}
              disabled={locked}
              className="text-[11px] px-2 py-1 rounded-full border transition-colors hover:border-[color:var(--fl-orange)] disabled:opacity-50"
              style={{ borderColor: "var(--fl-border)", color: "var(--fl-muted)" }}
              data-testid={`rom-add-${m.movement.replace(/\s+/g, "-")}`}
            >
              + {m.movement}{m.typical_deg ? ` · ${m.typical_deg}°` : ""}
            </button>
          ))}
        </div>
      </div>

      {(rom || []).length === 0 && (
        <div className="text-xs italic p-4 rounded-md text-center" style={{ color: "var(--fl-muted-2)", border: "1px dashed var(--fl-border)" }}>
          {t("assess.romNoEntries")}
        </div>
      )}

      {(rom || []).length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="rom-table">
            <thead>
              <tr style={{ color: "var(--fl-muted-2)", borderBottom: "1px solid var(--fl-border)" }}>
                <th className="text-left py-2 pr-2">{t("assess.romColHeaders.movement")}</th>
                <th className="text-left px-2">{t("assess.romColHeaders.side")}</th>
                <th className="text-left px-2">{t("assess.romColHeaders.arom")}</th>
                <th className="text-left px-2">{t("assess.romColHeaders.prom")}</th>
                <th className="text-left px-2">{t("assess.romColHeaders.typical")}</th>
                <th className="text-left px-2">{t("assess.romColHeaders.pain")}</th>
                <th className="text-left px-2">{t("assess.romColHeaders.endFeel")}</th>
                <th className="text-left px-2">{t("assess.romColHeaders.method")}</th>
                <th className="text-left px-2">{t("assess.romColHeaders.previous")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rom.map((r) => (
                <tr key={r.id} style={{ borderBottom: "1px solid var(--fl-border)" }}>
                  <td className="py-2 pr-2">
                    <div className="font-medium">{r.movement}</div>
                    <div className="text-[10px]" style={{ color: "var(--fl-muted-2)" }}>{r.region?.replace(/_/g, " ")}</div>
                  </td>
                  <td className="px-2">
                    <select
                      value={r.side}
                      onChange={(e) => update(r.id, { side: e.target.value })}
                      disabled={locked}
                      className="fl-input text-xs px-1 py-1 rounded"
                    >
                      <option value="R">R</option><option value="L">L</option><option value="B">Bilat</option><option value="N/A">N/A</option>
                    </select>
                  </td>
                  <td className="px-2">
                    <input
                      type="number"
                      value={r.arom}
                      onChange={(e) => update(r.id, { arom: e.target.value })}
                      disabled={locked}
                      className="fl-input text-xs px-1 py-1 rounded w-16"
                      style={{ color: colorForDelta(r) }}
                      data-testid={`rom-arom-${r.id.slice(0, 8)}`}
                    />
                  </td>
                  <td className="px-2">
                    <input type="number" value={r.prom} onChange={(e) => update(r.id, { prom: e.target.value })} disabled={locked}
                           className="fl-input text-xs px-1 py-1 rounded w-16" />
                  </td>
                  <td className="px-2" style={{ color: "var(--fl-muted-2)" }}>
                    {r.typical_deg != null ? `${r.typical_deg}°` : "—"}
                  </td>
                  <td className="px-2">
                    <input type="number" min={0} max={10} value={r.pain} onChange={(e) => update(r.id, { pain: e.target.value })} disabled={locked}
                           className="fl-input text-xs px-1 py-1 rounded w-14" placeholder="0-10" />
                  </td>
                  <td className="px-2">
                    <select value={r.end_feel} onChange={(e) => update(r.id, { end_feel: e.target.value })} disabled={locked}
                            className="fl-input text-xs px-1 py-1 rounded">
                      <option value="">—</option>
                      {(refs?.end_feels || []).map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </td>
                  <td className="px-2">
                    <select value={r.method} onChange={(e) => update(r.id, { method: e.target.value })} disabled={locked}
                            className="fl-input text-xs px-1 py-1 rounded">
                      <option value="">—</option>
                      {(refs?.methods || []).map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </td>
                  <td className="px-2">
                    <input type="number" value={r.previous} onChange={(e) => update(r.id, { previous: e.target.value })} disabled={locked}
                           className="fl-input text-xs px-1 py-1 rounded w-14" placeholder="—" />
                  </td>
                  <td className="pl-2">
                    {!locked && (
                      <button type="button" onClick={() => remove(r.id)} className="text-red-500 text-[10px] hover:underline">{t("assess.romRemove")}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* =========================================================================
   Repeatable-row list (Strength / Functional tests)
   ========================================================================= */
function RepeatableRows({ rows, setRows, template, locked, testid }) {
  const { t } = useTranslation();
  const add = () => setRows([...(rows || []), { id: crypto.randomUUID(), ...template }]);
  const update = (id, patch) => setRows((rows || []).map((r) => r.id === id ? { ...r, ...patch } : r));
  const remove = (id) => setRows((rows || []).filter((r) => r.id !== id));
  const cols = Object.keys(template);

  return (
    <div className="space-y-3" data-testid={testid}>
      {(rows || []).length === 0 ? (
        <div className="text-xs italic p-4 rounded-md text-center" style={{ color: "var(--fl-muted-2)", border: "1px dashed var(--fl-border)" }}>
          {t("assess.noEntries")}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3 rounded-md" style={{ border: "1px solid var(--fl-border)" }}>
              {cols.map((k) => (
                <label key={k} className="text-xs block">
                  <span className="block mb-0.5 capitalize" style={{ color: "var(--fl-muted)" }}>{k.replace(/_/g, " ")}</span>
                  <input
                    value={r[k] ?? ""}
                    onChange={(e) => update(r.id, { [k]: e.target.value })}
                    disabled={locked}
                    className="fl-input w-full text-xs px-2 py-1 rounded"
                  />
                </label>
              ))}
              {!locked && (
                <div className="col-span-2 md:col-span-4 text-right">
                  <button type="button" onClick={() => remove(r.id)} className="text-red-500 text-[10px] hover:underline">{t("assess.romRemove")}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {!locked && (
        <button type="button" onClick={add} className="fl-btn-ghost text-xs px-3 py-1.5 rounded-md inline-flex items-center gap-1" data-testid={`${testid}-add`}>
          <Plus size={12} /> {t("assess.addRow")}
        </button>
      )}
    </div>
  );
}

/* =========================================================================
   Page
   ========================================================================= */
export default function ClinicianAssessment() {
  const { t } = useTranslation();
  const [patients, setPatients] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [current, setCurrent] = useState(null);
  const [refs, setRefs] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [addendumOpen, setAddendumOpen] = useState(false);
  const [presetPatient, setPresetPatient] = useState("");
  const [originPatient, setOriginPatient] = useState(null);
  const [sp, setSp] = useSearchParams();
  const deepDone = useRef(false);

  useEffect(() => {
    if (deepDone.current) return;
    const pid = sp.get("patient");
    if (!pid) return;
    if (!patients.length) return; // wait for caseload so names resolve
    deepDone.current = true;
    // Remember where we came from so we can offer a clear "back to patient
    // profile" return path even after the deep-link params are cleared.
    setOriginPatient(pid);
    const isWalkin = sp.get("walkin") === "1";
    const isNew = sp.get("new") === "1";
    const clear = () => setSp({}, { replace: true });
    if (isWalkin) {
      (async () => {
        try {
          const r = await api.post("/assessments", { patient_id: pid, assessment_type: "initial", mode: "in_person" });
          setCurrent(r.data);
          api.get("/assessments/mine").then((x) => setAssessments(x.data)).catch(() => {});
          toast.success("New clinical note started");
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not start note"); }
        clear();
      })();
    } else if (isNew) {
      setPresetPatient(pid); setCreating(true); clear();
    } else {
      const pname = patients.find((p) => p.id === pid)?.name;
      if (pname) setQ(pname);
      clear();
    }
  }, [patients, sp]);

  useEffect(() => {
    api.get("/patients").then((r) => setPatients(r.data)).catch(() => {});
    api.get("/assessments/mine").then((r) => setAssessments(r.data)).catch(() => setAssessments([]));
    api.get("/assessments/refs/rom").then((r) => setRefs(r.data)).catch(() => {});
  }, []);

  const reloadList = () =>
    api.get("/assessments/mine").then((r) => setAssessments(r.data)).catch(() => {});

  const openAssessment = async (id) => {
    const r = await api.get(`/assessments/${id}`);
    setCurrent(r.data);
    setShowAudit(false);
  };

  // Deep-link: /clinician/assessment?assessment=<id> opens that assessment
  // directly (used by the Patient Intakes & screening-referral rows).
  useEffect(() => {
    const aid = sp.get("assessment");
    if (!aid || deepDone.current) return;
    deepDone.current = true;
    openAssessment(aid).catch(() => toast.error("Could not open assessment"));
    setSp({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);


  const filtered = useMemo(() => {
    return assessments.filter((a) => {
      if (filterStatus && a.status !== filterStatus) return false;
      if (q && !(`${a.patient_name} ${a.service} ${a.assessment_type}`.toLowerCase().includes(q.toLowerCase()))) return false;
      return true;
    });
  }, [assessments, filterStatus, q]);

  if (current) {
    return (
      <AssessmentEditor
        assessment={current}
        setAssessment={setCurrent}
        refs={refs}
        onBack={() => { setCurrent(null); reloadList(); }}
        originPatient={originPatient}
        originPatientName={patients.find((p) => p.id === originPatient)?.name}
        onSign={() => setSignOpen(true)}
        onAddendum={() => setAddendumOpen(true)}
        showAudit={showAudit}
        setShowAudit={setShowAudit}
        signOpen={signOpen}
        setSignOpen={setSignOpen}
        addendumOpen={addendumOpen}
        setAddendumOpen={setAddendumOpen}
      />
    );
  }

  return (
    <DashLayout>
      {originPatient && (
        <div className="mb-4 flex items-center justify-between gap-2 flex-wrap" data-testid="notes-origin-breadcrumb">
          <div className="text-xs text-zinc-500 flex items-center gap-1.5 flex-wrap">
            <span className="truncate max-w-[12rem]">{patients.find((p) => p.id === originPatient)?.name || "Patient"}</span>
            <span className="text-zinc-600">›</span>
            <span className="text-[#f47a20] font-semibold">Assessments &amp; Notes</span>
          </div>
          <Link to={`/clinician/patient/${originPatient}?tab=notes`} className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1" data-testid="notes-back-to-patient">
            <ArrowLeft size={13} /> Back to patient profile
          </Link>
        </div>
      )}
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="fl-label text-[#f47a20]">{t("assess.eyebrow")}</div>
          <h1 className="fl-heading text-4xl mt-2">{t("assess.h1")}</h1>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--fl-muted)" }}>
            {t("assess.subtitle")}
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="fl-btn px-4 py-2 rounded-md inline-flex items-center gap-2"
          data-testid="btn-new-assessment"
        >
          <Plus size={16} /> {t("assess.startNew")}
        </button>
      </div>

      {/* Filters */}
      <div className="fl-card p-4 mb-6 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]" style={{ color: "var(--fl-muted)" }}>
          <Search size={14} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("assess.searchPlaceholder")}
            className="fl-input w-full text-sm px-3 py-1.5 rounded-md"
            data-testid="filter-search"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {["", "draft", "completed", "signed", "locked", "corrected"].map((s) => (
            <button
              key={s || "all"}
              onClick={() => setFilterStatus(s)}
              className="px-2 py-1 text-xs rounded-full border transition-colors"
              style={{
                background: filterStatus === s ? "var(--fl-orange)" : "transparent",
                color: filterStatus === s ? "#111" : "var(--fl-muted)",
                borderColor: filterStatus === s ? "var(--fl-orange)" : "var(--fl-border)",
              }}
              data-testid={`filter-${s || "all"}`}
            >
              {s ? t(`assess.status.${s}`) : t("assess.filterAll")}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="fl-card p-10 text-center text-sm" style={{ color: "var(--fl-muted-2)" }}>
            {t("assess.empty")}
          </div>
        )}
        {filtered.map((a) => (
          <button
            key={a.id}
            onClick={() => openAssessment(a.id)}
            className="fl-card p-4 w-full text-left hover:border-[color:var(--fl-orange)] transition-all"
            data-testid={`row-assessment-${a.id.slice(0, 8)}`}
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="fl-heading text-base">{a.patient_name}</span>
                  <StatusBadge status={a.status} />
                  <span className="text-xs" style={{ color: "var(--fl-muted-2)" }}>
                    · {t(`assess.types.${a.assessment_type}`, a.assessment_type)}
                  </span>
                </div>
                <div className="text-xs mt-1" style={{ color: "var(--fl-muted)" }}>
                  {a.service || "—"} · {a.body_area || "—"} · {a.mode === "remote" ? t("assess.mode.remote") : t("assess.mode.in_person")}
                </div>
              </div>
              <div className="text-right text-xs" style={{ color: "var(--fl-muted-2)" }}>
                <div>{t("assess.createdShort")} {new Date(a.created_at).toLocaleDateString()}</div>
                {a.signed_at ? (
                  <div>{a.status === "locked" ? t("assess.lockedShort", "Locked") : t("assess.signedShort", "Signed")} {new Date(a.signed_at).toLocaleDateString()}</div>
                ) : a.updated_at ? (
                  <div>{t("assess.updatedShort", "Updated")} {new Date(a.updated_at).toLocaleDateString()}</div>
                ) : null}
              </div>
            </div>
          </button>
        ))}
      </div>

      {creating && (
        <NewAssessmentModal
          patients={patients}
          initialPatientId={presetPatient}
          onClose={() => { setCreating(false); setPresetPatient(""); }}
          onCreated={(a) => { setCreating(false); setPresetPatient(""); setCurrent(a); reloadList(); }}
        />
      )}
    </DashLayout>
  );
}

/* =========================================================================
   New Assessment modal
   ========================================================================= */
function NewAssessmentModal({ patients, onClose, onCreated, initialPatientId }) {
  const { t } = useTranslation();
  const [patientId, setPatientId] = useState(initialPatientId || "");
  const [type, setType] = useState("initial");
  const [mode, setMode] = useState("in_person");
  const [service, setService] = useState("");
  const [bodyArea, setBodyArea] = useState("");
  const [busy, setBusy] = useState(false);
  const [patientQuery, setPatientQuery] = useState("");

  const filteredPatients = patients.filter((p) =>
    `${p.name} ${p.email}`.toLowerCase().includes(patientQuery.trim().toLowerCase()));

  const submit = async () => {
    if (!patientId) return toast.error(t("assess.patient") + " ?");
    setBusy(true);
    try {
      const r = await api.post("/assessments", { patient_id: patientId, assessment_type: type, mode, service, body_area: bodyArea });
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    } finally { setBusy(false); }
  };

  const typeOpts = ASSESSMENT_TYPES.map((o) => ({ value: o.value, label: t(`assess.types.${o.value}`, o.label) }));
  const areaOpts = BODY_AREAS.map((a) => ({ value: a, label: t(`assess.areas.${a}`, a) }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="fl-card p-6 max-w-lg w-full" data-testid="new-assessment-modal">
        <div className="fl-heading text-xl mb-1">{t("assess.newTitle")}</div>
        <div className="text-xs mb-4" style={{ color: "var(--fl-muted)" }}>{t("assess.newHint")}</div>
        <div className="space-y-3">
          <Field label={t("assess.patient")} required testid="field-patient">
            <input
              type="text"
              value={patientQuery}
              onChange={(e) => setPatientQuery(e.target.value)}
              placeholder={`Search ${patients.length} patients by name or email…`}
              className="fl-input w-full text-sm px-3 py-2 rounded-md mb-2"
              data-testid="patient-search"
            />
            <Select value={patientId} onChange={setPatientId} testid="select-patient"
                    options={filteredPatients.map((p) => ({ value: p.id, label: `${p.name} · ${p.email}` }))} />
            <div className="text-[11px] mt-1" style={{ color: "var(--fl-muted)" }} data-testid="patient-count">
              {filteredPatients.length} of {patients.length} patients
            </div>
          </Field>
          <Field label={t("assess.assessmentType")} required testid="field-type">
            <Select value={type} onChange={setType} options={typeOpts} testid="select-type" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("assess.sections.appointment").split(" ")[0]} testid="field-mode">
              <Select value={mode} onChange={setMode} options={[{ value: "in_person", label: t("assess.mode.in_person") }, { value: "remote", label: t("assess.mode.remote") }]} testid="select-mode" />
            </Field>
            <Field label={t("assess.bodyArea")} testid="field-area">
              <Select value={bodyArea} onChange={setBodyArea} options={areaOpts} testid="select-area" />
            </Field>
          </div>
          <Field label={t("assess.service")} testid="field-service">
            <Select value={service} onChange={setService} options={SERVICES} testid="select-service" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="fl-btn-ghost px-4 py-2 rounded-md text-sm" data-testid="btn-cancel-new">{t("common.cancel", "Cancel")}</button>
          <button onClick={submit} disabled={busy} className="fl-btn px-4 py-2 rounded-md text-sm" data-testid="btn-create-assessment">
            {busy ? t("assess.creating") : t("assess.createDraft")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   Editor — the big form
   ========================================================================= */
function AssessmentEditor({ assessment, setAssessment, refs, onBack, originPatient, originPatientName, showAudit, setShowAudit,
                            signOpen, setSignOpen, addendumOpen, setAddendumOpen }) {
  const { t } = useTranslation();
  const baseLocked = ["signed", "locked", "corrected"].includes(assessment.status);
  const [amending, setAmending] = useState(false);
  const [amendMeta, setAmendMeta] = useState(null);
  const locked = baseLocked && !amending;
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Local editable copy — patch API is per-section for smaller payloads.
  const [local, setLocal] = useState(assessment);
  useEffect(() => { setLocal(assessment); setDirty(false); setAmending(false); setAmendMeta(null); }, [assessment.id, assessment.status]);

  const setField = (section, key, value) => {
    setLocal((prev) => ({ ...prev, [section]: { ...(prev[section] || {}), [key]: value } }));
    setDirty(true);
  };
  const setSection = (section, value) => {
    setLocal((prev) => ({ ...prev, [section]: value }));
    setDirty(true);
  };

  const saveDraft = async (status) => {
    setSaving(true);
    try {
      const payload = {
        appointment_type: local.appointment_type,
        mode: local.mode,
        service: local.service,
        body_area: local.body_area,
        intake: local.intake,
        subjective: local.subjective,
        objective: local.objective,
        rom: local.rom,
        strength: local.strength,
        functional: local.functional,
        outcome_measures: local.outcome_measures,
        clinical_impression: local.clinical_impression,
        treatment: local.treatment,
        plan: local.plan,
        followup: local.followup,
        reassessment: local.reassessment,
        discharge: local.discharge,
        consent: local.consent,
      };
      if (status) payload.status = status;
      const r = await api.patch(`/assessments/${local.id}`, payload);
      setAssessment(r.data);
      setDirty(false);
      toast.success(status === "completed" ? t("assess.markedComplete") : t("assess.draftSaved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const AMENDABLE = ["subjective", "objective", "clinical_impression", "plan", "treatment",
    "rom", "strength", "functional", "outcome_measures", "discharge", "reassessment"];
  const saveAmendment = async () => {
    if (!amendMeta?.reason || !amendMeta?.signature) return toast.error("Amendment reason and signature required");
    setSaving(true);
    try {
      const field_updates = {};
      AMENDABLE.forEach((s) => { if (local[s] !== undefined && local[s] !== null) field_updates[s] = local[s]; });
      const r = await api.post(`/assessments/${local.id}/amend`, {
        reason: amendMeta.reason, field_updates, clinician_signature_data_url: amendMeta.signature,
      });
      setAssessment(r.data);
      setAmending(false); setAmendMeta(null);
      toast.success("Amendment saved — new version recorded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Amendment failed");
    } finally { setSaving(false); }
  };

  const isType = (t) => local.assessment_type === t;
  const isSpinal = ["Cervical spine", "Thoracic spine", "Lumbar spine"].includes(local.body_area);

  return (
    <DashLayout>
      {/* Header strip */}
      <div className="mb-6">
        {originPatient && (
          <Link to={`/clinician/patient/${originPatient}?tab=notes`} className="inline-flex items-center gap-1 text-xs mb-2 text-zinc-400 hover:text-white" data-testid="notes-editor-back-to-patient">
            <ArrowLeft size={13} /> Back to {originPatientName || "patient"} profile
          </Link>
        )}
        <button onClick={onBack} className="inline-flex items-center gap-1 text-xs mb-3 hover:text-[color:var(--fl-orange)]" data-testid="btn-back">
          <ArrowLeft size={14} /> {t("assess.backList")}
        </button>
        <div className="fl-card p-4 md:p-5">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <ClipboardCheck size={18} className="text-[color:var(--fl-orange)]" />
                <div className="fl-heading text-xl">{local.patient_name}</div>
                <StatusBadge status={local.status} />
              </div>
              <div className="text-xs" style={{ color: "var(--fl-muted)" }}>
                {t(`assess.types.${local.assessment_type}`, local.assessment_type)}
                {" · "}{local.mode === "remote" ? t("assess.mode.remote") : t("assess.mode.in_person")}
                {local.service ? ` · ${local.service}` : ""}
                {local.body_area ? ` · ${t(`assess.areas.${local.body_area}`, local.body_area)}` : ""}
              </div>
            </div>
            <div className="text-xs text-right" style={{ color: "var(--fl-muted-2)" }}>
              <div><b style={{ color: "var(--fl-text)" }}>{local.clinician_name}</b> · {local.clinician_role}</div>
              {local.clinician_registration && <div>{t("assess.regNum")}{local.clinician_registration}</div>}
              <div>{t("assess.apptShort")}: {new Date(local.appointment_scheduled_at || local.created_at).toLocaleString()}</div>
              <div>{t("assess.createdShort")} {new Date(local.created_at).toLocaleString()}</div>
            </div>
          </div>
          {baseLocked && !amending && (
            <div className="mt-3 text-xs p-2 rounded-md inline-flex items-center gap-1"
                 style={{ background: "#11182722", border: "1px solid var(--fl-border)", color: "var(--fl-muted)" }}>
              <Lock size={12} /> {t("assess.lockedNotice")}
            </div>
          )}
          {amending && (
            <div className="mt-3 text-xs p-2 rounded-md flex items-start gap-2"
                 data-testid="amend-banner"
                 style={{ background: "color-mix(in oklab, #8B5CF6 10%, transparent)", border: "1px solid #8B5CF655", color: "#C4B5FD" }}>
              <Pencil size={12} className="mt-0.5" />
              <span>Amending signed record — only clinical findings (Subjective, Objective, Impression, Plan, Treatment, ROM, Strength, Functional, Outcomes, Reassessment, Discharge) will be saved. The original version is preserved and identity/intake/consent stay unchanged.</span>
            </div>
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {!baseLocked && (
            <>
              <button onClick={() => saveDraft(null)} disabled={saving} className="fl-btn-ghost px-3 py-1.5 text-xs rounded-md inline-flex items-center gap-1" data-testid="btn-save-draft">
                <Pencil size={12} /> {saving ? t("assess.saving") : t("assess.saveDraft")}
              </button>
              <button onClick={() => saveDraft("completed")} disabled={saving} className="fl-btn-ghost px-3 py-1.5 text-xs rounded-md inline-flex items-center gap-1" data-testid="btn-mark-complete">
                <CheckCircle2 size={12} /> {t("assess.markComplete")}
              </button>
              <button onClick={() => setSignOpen(true)} className="fl-btn px-3 py-1.5 text-xs rounded-md inline-flex items-center gap-1" data-testid="btn-open-sign">
                <FileSignature size={12} /> {t("assess.signLock")}
              </button>
            </>
          )}
          {baseLocked && !amending && (
            <button onClick={() => setAddendumOpen(true)} className="fl-btn px-3 py-1.5 text-xs rounded-md inline-flex items-center gap-1" data-testid="btn-open-amend">
              <ScrollText size={12} /> Amend note
            </button>
          )}
          {amending && (
            <>
              <button onClick={saveAmendment} disabled={saving} className="fl-btn px-3 py-1.5 text-xs rounded-md inline-flex items-center gap-1" data-testid="btn-save-amendment">
                <CheckCircle2 size={12} /> {saving ? t("assess.saving") : "Save amendment"}
              </button>
              <button onClick={() => { setAmending(false); setAmendMeta(null); setLocal(assessment); }} className="fl-btn-ghost px-3 py-1.5 text-xs rounded-md inline-flex items-center gap-1" data-testid="btn-cancel-amendment">
                Cancel amendment
              </button>
            </>
          )}
          <button onClick={() => setShowAudit((v) => !v)} className="fl-btn-ghost px-3 py-1.5 text-xs rounded-md inline-flex items-center gap-1" data-testid="btn-toggle-audit">
            <History size={12} /> {showAudit ? t("assess.hideAudit") : t("assess.viewAudit")}
          </button>
        </div>
      </div>

      {showAudit && <AuditPanel assessment={local} />}
      {showAudit && <VersionHistoryPanel assessment={local} />}

      <div className="space-y-3">
        <Section id="appointment" title={t("assess.sections.appointment")} icon={ClipboardList} defaultOpen>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label={t("assess.assessmentType")}><Select value={local.assessment_type} onChange={(v) => { setLocal((p) => ({ ...p, assessment_type: v })); setDirty(true); }} options={ASSESSMENT_TYPES.map((o) => ({ value: o.value, label: t(`assess.types.${o.value}`, o.label) }))} locked={baseLocked} /></Field>
            <Field label={t("assess.mode.in_person")}><Select value={local.mode} onChange={(v) => { setLocal((p) => ({ ...p, mode: v })); setDirty(true); }} options={[{ value: "in_person", label: t("assess.mode.in_person") }, { value: "remote", label: t("assess.mode.remote") }]} locked={baseLocked} /></Field>
            <Field label={t("assess.service")}><Select value={local.service || ""} onChange={(v) => { setLocal((p) => ({ ...p, service: v })); setDirty(true); }} options={SERVICES} locked={baseLocked} /></Field>
            <Field label={t("assess.bodyArea")}><Select value={local.body_area || ""} onChange={(v) => { setLocal((p) => ({ ...p, body_area: v })); setDirty(true); }} options={BODY_AREAS.map((a) => ({ value: a, label: t(`assess.areas.${a}`, a) }))} locked={baseLocked} /></Field>
          </div>
        </Section>

        <Section id="intake" title={t("assess.sections.intake")} icon={User}>
          <div className="text-[11px] p-2 rounded-md" style={{ background: "color-mix(in oklab, #3B82F6 8%, transparent)", color: "#60A5FA", border: "1px solid #3B82F644" }}>
            {t("assess.intakeBanner")}
            {local.intake?._submitted_at && (
              <div className="mt-1 text-[10px]" style={{ color: "var(--fl-muted-2)" }}>
                <b>{t("assess.submittedBy")}</b> {new Date(local.intake._submitted_at).toLocaleString()}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={t("assess.fields.mainProblem")}><Textarea value={local.intake?.main_problem} onChange={(v) => setField("intake", "main_problem", v)} locked={locked} testid="intake-main-problem" /></Field>
            <Field label={t("assess.fields.reasonBooking")}><Textarea value={local.intake?.reason_booking} onChange={(v) => setField("intake", "reason_booking", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.symptomsStarted")}><Input type="date" value={local.intake?.symptoms_started} onChange={(v) => setField("intake", "symptoms_started", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.mechanism")}><Input value={local.intake?.mechanism} onChange={(v) => setField("intake", "mechanism", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.painNow")}><Input type="number" min={0} max={10} value={local.intake?.pain_now} onChange={(v) => setField("intake", "pain_now", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.painWorst")}><Input type="number" min={0} max={10} value={local.intake?.pain_worst} onChange={(v) => setField("intake", "pain_worst", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.aggravating")}><Textarea value={local.intake?.aggravating} onChange={(v) => setField("intake", "aggravating", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.easing")}><Textarea value={local.intake?.easing} onChange={(v) => setField("intake", "easing", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.conditions")}><Textarea value={local.intake?.conditions} onChange={(v) => setField("intake", "conditions", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.medication")}><Textarea value={local.intake?.medication} onChange={(v) => setField("intake", "medication", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.allergies")}><Input value={local.intake?.allergies} onChange={(v) => setField("intake", "allergies", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.occupation")}><Input value={local.intake?.occupation} onChange={(v) => setField("intake", "occupation", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.sport")}><Input value={local.intake?.sport} onChange={(v) => setField("intake", "sport", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.activityLevel")}><Input value={local.intake?.activity_level} onChange={(v) => setField("intake", "activity_level", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.shortGoals")}><Textarea value={local.intake?.short_goals} onChange={(v) => setField("intake", "short_goals", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.longGoals")}><Textarea value={local.intake?.long_goals} onChange={(v) => setField("intake", "long_goals", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.emergencyContact")}><Input value={local.intake?.emergency_contact} onChange={(v) => setField("intake", "emergency_contact", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.gp")}><Input value={local.intake?.gp} onChange={(v) => setField("intake", "gp", v)} locked={locked} /></Field>
          </div>
          {(local.intake?.body_map?.length > 0) && (
            <div className="mt-2">
              <div className="fl-heading text-xs mb-2 flex items-center gap-2">
                <MapPin size={12} className="text-[color:var(--fl-orange)]" /> {t("assess.patientMarkedSymptoms")}
              </div>
              <BodyMap value={local.intake.body_map} onChange={(v) => setField("intake", "body_map", v)} locked={locked} testid="intake-bodymap-view" />
            </div>
          )}
        </Section>

        {isType("initial") && (
          <Section id="subjective" title={t("assess.sections.subjective")} icon={Stethoscope} defaultOpen>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label={t("assess.fields.presenting")} required><Textarea value={local.subjective?.presenting} onChange={(v) => setField("subjective", "presenting", v)} locked={locked} testid="subj-presenting" /></Field>
              <Field label={t("assess.fields.hx")}><Textarea value={local.subjective?.hx} onChange={(v) => setField("subjective", "hx", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.onsetDate")}><Input type="date" value={local.subjective?.onset_date} onChange={(v) => setField("subjective", "onset_date", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.moi")}><Input value={local.subjective?.moi} onChange={(v) => setField("subjective", "moi", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.location")}><Input value={local.subjective?.location} onChange={(v) => setField("subjective", "location", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.distribution")}><Input value={local.subjective?.distribution} onChange={(v) => setField("subjective", "distribution", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.nature")}><Input value={local.subjective?.nature} onChange={(v) => setField("subjective", "nature", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.severity")}><Input type="number" min={0} max={10} value={local.subjective?.severity} onChange={(v) => setField("subjective", "severity", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.irritability")}><Select value={local.subjective?.irritability} onChange={(v) => setField("subjective", "irritability", v)} options={[t("assess.choices.low"), t("assess.choices.moderate"), t("assess.choices.high")]} locked={locked} /></Field>
              <Field label={t("assess.fields.agg")}><Textarea value={local.subjective?.agg} onChange={(v) => setField("subjective", "agg", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.easing")}><Textarea value={local.subjective?.easing} onChange={(v) => setField("subjective", "easing", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.pattern24h")}><Textarea value={local.subjective?.pattern_24h} onChange={(v) => setField("subjective", "pattern_24h", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.nightPain")}><ChoiceRow value={local.subjective?.night_pain} onChange={(v) => setField("subjective", "night_pain", v)} locked={locked} options={[t("assess.choices.yes"), t("assess.choices.no"), t("assess.choices.na"), t("assess.choices.notAssessed"), t("assess.choices.noConcerns")]} /></Field>
              <Field label={t("assess.fields.morningStiff")}><ChoiceRow value={local.subjective?.morning_stiff} onChange={(v) => setField("subjective", "morning_stiff", v)} locked={locked} options={[t("assess.choices.yes"), t("assess.choices.no"), t("assess.choices.na"), t("assess.choices.notAssessed"), t("assess.choices.noConcerns")]} /></Field>
              <Field label={t("assess.fields.prevTx")}><Textarea value={local.subjective?.prev_tx} onChange={(v) => setField("subjective", "prev_tx", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.prevResp")}><Textarea value={local.subjective?.prev_response} onChange={(v) => setField("subjective", "prev_response", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.medicalHx")}><Textarea value={local.subjective?.medical_hx} onChange={(v) => setField("subjective", "medical_hx", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.surgicalHx")}><Textarea value={local.subjective?.surgical_hx} onChange={(v) => setField("subjective", "surgical_hx", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.medication")}><Textarea value={local.subjective?.medication} onChange={(v) => setField("subjective", "medication", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.allergies")}><Input value={local.subjective?.allergies} onChange={(v) => setField("subjective", "allergies", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.imaging")}><Textarea value={local.subjective?.imaging} onChange={(v) => setField("subjective", "imaging", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.yellowFlags")}><Textarea value={local.subjective?.yellow_flags} onChange={(v) => setField("subjective", "yellow_flags", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.occupational")}><Input value={local.subjective?.occupational} onChange={(v) => setField("subjective", "occupational", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.sportDemands")}><Input value={local.subjective?.sport_demands} onChange={(v) => setField("subjective", "sport_demands", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.goals")} required><Textarea value={local.subjective?.goals} onChange={(v) => setField("subjective", "goals", v)} locked={locked} /></Field>
            </div>

            {/* Interactive body map for symptom localisation */}
            <div className="fl-card p-3 md:p-4">
              <div className="flex items-center gap-2 mb-2">
                <MapPin size={14} className="text-[color:var(--fl-orange)]" />
                <div className="fl-heading text-sm">{t("assess.bodyMapTitle")}</div>
              </div>
              <BodyMap
                value={local.subjective?.body_map || local.intake?.body_map || []}
                onChange={(v) => setField("subjective", "body_map", v)}
                locked={locked}
                testid="subj-bodymap"
              />
            </div>

            {/* Conditional: spinal red flags */}
            {isSpinal && (
              <div className="fl-card p-3 md:p-4" style={{ background: "color-mix(in oklab, #EF4444 6%, transparent)", borderColor: "#EF444455" }}>
                <div className="flex items-center gap-2 mb-3 text-red-500">
                  <AlertTriangle size={14} /> <div className="fl-heading text-sm">{t("assess.redFlagsTitle")}</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {t("assess.redFlags", { returnObjects: true }).map((k, i) => (
                    <Field key={i} label={k}>
                      <ChoiceRow value={local.subjective?.[`rf_${i}`]} onChange={(v) => setField("subjective", `rf_${i}`, v)} locked={locked} options={[t("assess.choices.noConcerns"), t("assess.choices.yes"), t("assess.choices.deferred")]} />
                    </Field>
                  ))}
                </div>
              </div>
            )}

            {/* Conditional: post-op */}
            {isType("post_op") && (
              <div className="fl-card p-3 md:p-4">
                <div className="fl-heading text-sm mb-3">{t("assess.postOpTitle")}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label={t("assess.postOp.procedure")}><Input value={local.subjective?.postop_procedure} onChange={(v) => setField("subjective", "postop_procedure", v)} locked={locked} /></Field>
                  <Field label={t("assess.postOp.date")}><Input type="date" value={local.subjective?.postop_date} onChange={(v) => setField("subjective", "postop_date", v)} locked={locked} /></Field>
                  <Field label={t("assess.postOp.surgeon")}><Input value={local.subjective?.postop_surgeon} onChange={(v) => setField("subjective", "postop_surgeon", v)} locked={locked} /></Field>
                  <Field label={t("assess.postOp.hospital")}><Input value={local.subjective?.postop_hospital} onChange={(v) => setField("subjective", "postop_hospital", v)} locked={locked} /></Field>
                  <Field label={t("assess.postOp.wb")}><Input value={local.subjective?.postop_wb} onChange={(v) => setField("subjective", "postop_wb", v)} locked={locked} /></Field>
                  <Field label={t("assess.postOp.restrictions")}><Textarea value={local.subjective?.postop_restrictions} onChange={(v) => setField("subjective", "postop_restrictions", v)} locked={locked} /></Field>
                  <Field label={t("assess.postOp.protocol")}><Textarea value={local.subjective?.postop_protocol} onChange={(v) => setField("subjective", "postop_protocol", v)} locked={locked} /></Field>
                  <Field label={t("assess.postOp.complications")}><Textarea value={local.subjective?.postop_complications} onChange={(v) => setField("subjective", "postop_complications", v)} locked={locked} /></Field>
                </div>
              </div>
            )}

            {/* Conditional: remote */}
            {(local.mode === "remote" || isType("remote_consultation")) && (
              <div className="fl-card p-3 md:p-4">
                <div className="fl-heading text-sm mb-3">{t("assess.remoteTitle")}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label={t("assess.remote.location")}><Input value={local.subjective?.remote_location} onChange={(v) => setField("subjective", "remote_location", v)} locked={locked} /></Field>
                  <Field label={t("assess.remote.emergencyOk")}><ChoiceRow value={local.subjective?.remote_emg} onChange={(v) => setField("subjective", "remote_emg", v)} options={[t("assess.choices.yes"), t("assess.choices.no")]} locked={locked} /></Field>
                  <Field label={t("assess.remote.envSafe")}><ChoiceRow value={local.subjective?.remote_safe} onChange={(v) => setField("subjective", "remote_safe", v)} options={[t("assess.choices.yes"), t("assess.choices.no")]} locked={locked} /></Field>
                  <Field label={t("assess.remote.camOk")}><ChoiceRow value={local.subjective?.remote_cam} onChange={(v) => setField("subjective", "remote_cam", v)} options={[t("assess.choices.yes"), t("assess.choices.no")]} locked={locked} /></Field>
                  <Field label={t("assess.remote.limits")}><Textarea value={local.subjective?.remote_limits} onChange={(v) => setField("subjective", "remote_limits", v)} locked={locked} /></Field>
                </div>
              </div>
            )}
          </Section>
        )}

        {/* Objective — for initial and reassessment */}
        {(isType("initial") || isType("reassessment") || isType("return_to_sport") || isType("running_assessment") || isType("post_op") || isType("sc_assessment")) && (
          <Section id="objective" title={t("assess.sections.objective")} icon={Activity}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label={t("assess.fields.observation")}><Textarea value={local.objective?.observation} onChange={(v) => setField("objective", "observation", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.palpation")}><Textarea value={local.objective?.palpation} onChange={(v) => setField("objective", "palpation", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.swelling")}><Textarea value={local.objective?.swelling} onChange={(v) => setField("objective", "swelling", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.neuro")}><Textarea value={local.objective?.neuro} onChange={(v) => setField("objective", "neuro", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.specialTests")}><Textarea value={local.objective?.special_tests} onChange={(v) => setField("objective", "special_tests", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.functionalMv")}><Textarea value={local.objective?.functional} onChange={(v) => setField("objective", "functional", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.balance")}><Textarea value={local.objective?.balance} onChange={(v) => setField("objective", "balance", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.notes")}><Textarea value={local.objective?.notes} onChange={(v) => setField("objective", "notes", v)} locked={locked} /></Field>
            </div>
          </Section>
        )}

        {/* ROM */}
        {(isType("initial") || isType("reassessment") || isType("return_to_sport") || isType("running_assessment") || isType("post_op") || isType("discharge")) && (
          <Section id="rom" title={t("assess.sections.rom")} icon={Ruler}>
            <ROMTable rom={local.rom} setRom={(v) => setSection("rom", v)} refs={refs} locked={locked} />
          </Section>
        )}

        {/* Strength */}
        {(isType("initial") || isType("reassessment") || isType("return_to_sport") || isType("sc_assessment") || isType("post_op") || isType("discharge")) && (
          <Section id="strength" title={t("assess.sections.strength")} icon={Activity}>
            <TestLibraryPicker kind="strength" onAdd={(row) => setSection("strength", [...(local.strength || []), row])} locked={locked} />
            <RepeatableRows
              rows={local.strength}
              setRows={(v) => setSection("strength", v)}
              locked={locked}
              testid="strength"
              template={{ muscle: "", side: "R", method: "", position: "", value: "", unit: "kg", pain: "", previous: "", lsi: "", target: "", notes: "" }}
            />
          </Section>
        )}

        {/* Functional */}
        {(isType("initial") || isType("reassessment") || isType("return_to_sport") || isType("running_assessment") || isType("sc_assessment") || isType("discharge")) && (
          <Section id="functional" title={t("assess.sections.functional")} icon={Target}>
            <TestLibraryPicker kind="functional" onAdd={(row) => setSection("functional", [...(local.functional || []), row])} locked={locked} />
            <RepeatableRows
              rows={local.functional}
              setRows={(v) => setSection("functional", v)}
              locked={locked}
              testid="functional"
              template={{ test: "", protocol: "", side: "", value: "", unit: "", pain: "", quality: "", previous: "", target: "", pass_fail: "", notes: "" }}
            />
          </Section>
        )}

        {/* Reassessment / Discharge — time-series comparison */}
        {(isType("reassessment") || isType("discharge")) && (
          <Section id="progress" title={isType("discharge") ? t("assess.sections.progressDischarge") : t("assess.sections.progressReass")} icon={BarChart3} defaultOpen>
            <ReassessmentCharts patientId={local.patient_id} currentAssessmentId={local.id} />
          </Section>
        )}

        {/* Outcome measures */}
        <Section id="outcomes" title={t("assess.sections.outcomes")} icon={FilePlus}>
          <RepeatableRows
            rows={local.outcome_measures}
            setRows={(v) => setSection("outcome_measures", v)}
            locked={locked}
            testid="outcomes"
            template={{ name: "", score: "", scale: "", previous: "", change: "", notes: "" }}
          />
        </Section>

        {/* Clinical impression */}
        {(isType("initial") || isType("reassessment")) && (
          <Section id="impression" title={t("assess.sections.impression")} icon={Stethoscope} required>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label={t("assess.fields.workingDx")} required><Textarea value={local.clinical_impression?.working_dx} onChange={(v) => setField("clinical_impression", "working_dx", v)} locked={locked} testid="working-dx" /></Field>
              <Field label={t("assess.fields.differentials")}><Textarea value={local.clinical_impression?.differentials} onChange={(v) => setField("clinical_impression", "differentials", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.mainFindings")}><Textarea value={local.clinical_impression?.main_findings} onChange={(v) => setField("clinical_impression", "main_findings", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.contributors")}><Textarea value={local.clinical_impression?.contributors} onChange={(v) => setField("clinical_impression", "contributors", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.problems")}><Textarea value={local.clinical_impression?.problems} onChange={(v) => setField("clinical_impression", "problems", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.redFlags")}><Textarea value={local.clinical_impression?.red_flags} onChange={(v) => setField("clinical_impression", "red_flags", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.potential")}><Textarea value={local.clinical_impression?.potential} onChange={(v) => setField("clinical_impression", "potential", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.prognosis")}><Textarea value={local.clinical_impression?.prognosis} onChange={(v) => setField("clinical_impression", "prognosis", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.referral")}><Textarea value={local.clinical_impression?.referral} onChange={(v) => setField("clinical_impression", "referral", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.reasoning")}><Textarea value={local.clinical_impression?.reasoning} onChange={(v) => setField("clinical_impression", "reasoning", v)} locked={locked} rows={4} /></Field>
            </div>
          </Section>
        )}

        {/* Treatment */}
        <Section id="treatment" title={t("assess.sections.treatment")} icon={HeartHandshake}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={t("assess.fields.txProvided")}><Textarea value={local.treatment?.provided} onChange={(v) => setField("treatment", "provided", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.manual")}><Textarea value={local.treatment?.manual} onChange={(v) => setField("treatment", "manual", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.exercise")}><Textarea value={local.treatment?.exercise} onChange={(v) => setField("treatment", "exercise", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.education")}><Textarea value={local.treatment?.education} onChange={(v) => setField("treatment", "education", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.load")}><Textarea value={local.treatment?.load} onChange={(v) => setField("treatment", "load", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.hep")}><Textarea value={local.treatment?.hep} onChange={(v) => setField("treatment", "hep", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.response")}><Textarea value={local.treatment?.response} onChange={(v) => setField("treatment", "response", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.adverse")}><ChoiceRow value={local.treatment?.adverse} onChange={(v) => setField("treatment", "adverse", v)} options={[t("assess.choices.no"), t("assess.choices.yes"), t("assess.choices.na")]} locked={locked} /></Field>
          </div>
        </Section>

        {/* Plan */}
        <Section id="plan" title={t("assess.sections.plan")} icon={ClipboardCheck}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={t("assess.fields.shortGoals")}><Textarea value={local.plan?.short_goals} onChange={(v) => setField("plan", "short_goals", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.longGoals")}><Textarea value={local.plan?.long_goals} onChange={(v) => setField("plan", "long_goals", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.frequency")}><Input value={local.plan?.frequency} onChange={(v) => setField("plan", "frequency", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.duration")}><Input value={local.plan?.duration} onChange={(v) => setField("plan", "duration", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.repeatOutcomes")}><Textarea value={local.plan?.repeat_outcomes} onChange={(v) => setField("plan", "repeat_outcomes", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.nextAppt")}><Input type="datetime-local" value={local.plan?.next_appt} onChange={(v) => setField("plan", "next_appt", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.nextSession")}><Textarea value={local.plan?.next_session} onChange={(v) => setField("plan", "next_session", v)} locked={locked} /></Field>
            <Field label={t("assess.fields.restrictions")}><Textarea value={local.plan?.restrictions} onChange={(v) => setField("plan", "restrictions", v)} locked={locked} /></Field>
          </div>
        </Section>

        {/* Type-specific: Follow-up */}
        {isType("follow_up") && (
          <Section id="followup" title={t("assess.sections.followup")} icon={ClipboardList} defaultOpen>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label={t("assess.fields.change")}><Textarea value={local.followup?.change} onChange={(v) => setField("followup", "change", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.currentSymptoms")}><Textarea value={local.followup?.symptoms} onChange={(v) => setField("followup", "symptoms", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.currentPain")}><Input type="number" min={0} max={10} value={local.followup?.pain} onChange={(v) => setField("followup", "pain", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.response")}><Textarea value={local.followup?.response} onChange={(v) => setField("followup", "response", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.adherence")}><Textarea value={local.followup?.adherence} onChange={(v) => setField("followup", "adherence", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.newConcerns")}><Textarea value={local.followup?.new_concerns} onChange={(v) => setField("followup", "new_concerns", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.flAssessment")}><Textarea value={local.followup?.assessment} onChange={(v) => setField("followup", "assessment", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.barriers")}><Textarea value={local.followup?.barriers} onChange={(v) => setField("followup", "barriers", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.progressCat")}>
                <Select value={local.followup?.progress} onChange={(v) => setField("followup", "progress", v)}
                        options={t("assess.progressCats", { returnObjects: true })}
                        locked={locked} />
              </Field>
            </div>
          </Section>
        )}

        {/* Type-specific: Discharge */}
        {isType("discharge") && (
          <Section id="discharge" title={t("assess.sections.discharge")} icon={CheckCircle2} defaultOpen>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label={t("assess.fields.dischargeReason")}>
                <Select value={local.discharge?.reason} onChange={(v) => setField("discharge", "reason", v)}
                        options={t("assess.dischargeReasons", { returnObjects: true })}
                        locked={locked} />
              </Field>
              <Field label={t("assess.fields.goalsAchieved")}><Textarea value={local.discharge?.goals_achieved} onChange={(v) => setField("discharge", "goals_achieved", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.goalsPartial")}><Textarea value={local.discharge?.goals_partial} onChange={(v) => setField("discharge", "goals_partial", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.goalsNot")}><Textarea value={local.discharge?.goals_not} onChange={(v) => setField("discharge", "goals_not", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.rtw")}><ChoiceRow value={local.discharge?.rtw} onChange={(v) => setField("discharge", "rtw", v)} locked={locked} options={[t("assess.choices.full"), t("assess.choices.modified"), t("assess.choices.notYet")]} /></Field>
              <Field label={t("assess.fields.rts")}><ChoiceRow value={local.discharge?.rts} onChange={(v) => setField("discharge", "rts", v)} locked={locked} options={[t("assess.choices.full"), t("assess.choices.modified"), t("assess.choices.notYet")]} /></Field>
              <Field label={t("assess.fields.selfMgmt")}><Textarea value={local.discharge?.self_mgmt} onChange={(v) => setField("discharge", "self_mgmt", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.continueEx")}><Textarea value={local.discharge?.continue_ex} onChange={(v) => setField("discharge", "continue_ex", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.warning")}><Textarea value={local.discharge?.warning} onChange={(v) => setField("discharge", "warning", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.rebook")}><Textarea value={local.discharge?.rebook} onChange={(v) => setField("discharge", "rebook", v)} locked={locked} /></Field>
              <Field label={t("assess.fields.dischargeSummary")}><Textarea value={local.discharge?.summary} onChange={(v) => setField("discharge", "summary", v)} locked={locked} rows={4} /></Field>
            </div>
          </Section>
        )}

        {/* Consent */}
        <Section id="consent" title={t("assess.sections.consent")} icon={ShieldCheck} required>
          <div className="text-[11px] p-2 rounded-md mb-2" style={{ background: "color-mix(in oklab, #22C55E 6%, transparent)", color: "#22C55E", border: "1px solid #22C55E33" }}>
            {t("assess.consentBanner")}
          </div>
          {!locked && (() => {
            const CLINICAL_KEYS = ["assessment", "treatment", "manual_therapy", "massage", "exercise", "remote", "plan", "reconfirmed"];
            const PRIVACY_KEYS = ["privacy_notice", "email", "sms", "whatsapp", "gp_contact", "clinician_share", "coach_share", "employer_share", "apple_health", "research"];
            const PV_CLIN_KEYS = ["clinical_record", "movement_analysis", "progress_compare", "remote_review", "team_education"];
            const PV_MKT_KEYS = ["website", "social", "materials", "testimonials", "edu"];
            const GRANTED = t("assess.choices.granted");
            const grantAll = () => {
              if (!window.confirm("Set every consent item in this assessment to \"granted\"?\n\nUse this only when the patient has verbally confirmed consent for every category (clinical, privacy, photo/video clinical, photo/video marketing).")) return;
              const grantMap = (keys) => keys.reduce((acc, k) => { acc[k] = GRANTED; return acc; }, {});
              setSection("consent", {
                ...(local.consent || {}),
                clinical:     { ...(local.consent?.clinical     || {}), ...grantMap(CLINICAL_KEYS) },
                privacy:      { ...(local.consent?.privacy      || {}), ...grantMap(PRIVACY_KEYS) },
                pv_clinical:  { ...(local.consent?.pv_clinical  || {}), ...grantMap(PV_CLIN_KEYS) },
                pv_marketing: { ...(local.consent?.pv_marketing || {}), ...grantMap(PV_MKT_KEYS) },
              });
              toast.success("Consent gained across all categories");
            };
            return (
              <div className="flex justify-end mb-3">
                <button
                  type="button"
                  onClick={grantAll}
                  className="fl-btn text-xs"
                  data-testid="consent-all-gained"
                  title="Set every consent item to granted"
                >
                  <CheckCircle2 size={12} /> All gained
                </button>
              </div>
            );
          })()}
          <div>
            <div className="fl-heading text-sm mb-2">{t("assess.consentClinical")}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {["assessment", "treatment", "manual_therapy", "massage", "exercise", "remote", "plan", "reconfirmed"].map((k) => (
                <Field key={k} label={t(`assess.consent.${k}`)}>
                  <ChoiceRow
                    value={local.consent?.clinical?.[k]}
                    onChange={(v) => setSection("consent", { ...(local.consent || {}), clinical: { ...(local.consent?.clinical || {}), [k]: v } })}
                    options={[t("assess.choices.granted"), t("assess.choices.declined"), t("assess.choices.na")]}
                    locked={locked}
                    testid={`consent-clinical-${k}`}
                  />
                </Field>
              ))}
            </div>
          </div>
          <div>
            <div className="fl-heading text-sm mb-2 mt-4">{t("assess.consentPrivacy")}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {["privacy_notice", "email", "sms", "whatsapp", "gp_contact", "clinician_share", "coach_share", "employer_share", "apple_health", "research"].map((k) => (
                <Field key={k} label={t(`assess.consent.${k}`)}>
                  <ChoiceRow
                    value={local.consent?.privacy?.[k]}
                    onChange={(v) => setSection("consent", { ...(local.consent || {}), privacy: { ...(local.consent?.privacy || {}), [k]: v } })}
                    options={[t("assess.choices.granted"), t("assess.choices.declined"), t("assess.choices.withdrawn"), t("assess.choices.na")]}
                    locked={locked}
                  />
                </Field>
              ))}
            </div>
          </div>
          <div>
            <div className="fl-heading text-sm mb-2 mt-4">{t("assess.consentPVClinical")}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {["clinical_record", "movement_analysis", "progress_compare", "remote_review", "team_education"].map((k) => (
                <Field key={k} label={t(`assess.consent.${k}`)}>
                  <ChoiceRow
                    value={local.consent?.pv_clinical?.[k]}
                    onChange={(v) => setSection("consent", { ...(local.consent || {}), pv_clinical: { ...(local.consent?.pv_clinical || {}), [k]: v } })}
                    options={[t("assess.choices.granted"), t("assess.choices.declined"), t("assess.choices.na")]}
                    locked={locked}
                  />
                </Field>
              ))}
            </div>
            <div className="fl-heading text-sm mb-2 mt-4">{t("assess.consentPVMarketing")}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {["website", "social", "materials", "testimonials", "edu"].map((k) => (
                <Field key={k} label={t(`assess.consent.${k}`)}>
                  <ChoiceRow
                    value={local.consent?.pv_marketing?.[k]}
                    onChange={(v) => setSection("consent", { ...(local.consent || {}), pv_marketing: { ...(local.consent?.pv_marketing || {}), [k]: v } })}
                    options={[t("assess.choices.granted"), t("assess.choices.declined"), t("assess.choices.na")]}
                    locked={locked}
                  />
                </Field>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            <Field label={t("assess.consentVersion")}><Input value={local.consent?.version} onChange={(v) => setSection("consent", { ...(local.consent || {}), version: v })} locked={locked} placeholder="v1.0" /></Field>
            <Field label={t("assess.consentMethod")}>
              <Select value={local.consent?.method} onChange={(v) => setSection("consent", { ...(local.consent || {}), method: v })} options={t("assess.consentMethods", { returnObjects: true })} locked={locked} />
            </Field>
            <Field label={t("assess.consentSupport")}><Input value={local.consent?.support} onChange={(v) => setSection("consent", { ...(local.consent || {}), support: v })} locked={locked} /></Field>
          </div>
        </Section>

        {/* Signatures */}
        <Section id="signatures" title={t("assess.sections.signatures")} icon={FileSignature} required defaultOpen={local.status === "completed"}>
          <SignaturesBlock assessment={local} setAssessment={setAssessment} locked={locked} />
        </Section>
      </div>

      {signOpen && <SignLockModal assessment={local} setAssessment={setAssessment} onClose={() => setSignOpen(false)} />}
      {addendumOpen && <AmendStartModal onClose={() => setAddendumOpen(false)} onConfirm={(meta) => { setAmendMeta(meta); setAmending(true); setAddendumOpen(false); toast.message("Amend mode — edit clinical findings, then Save amendment"); }} />}
    </DashLayout>
  );
}

/* =========================================================================
   Signatures block (inline within Section)
   ========================================================================= */
function SignaturesBlock({ assessment, setAssessment, locked }) {
  const { t } = useTranslation();
  const [patientName, setPatientName] = useState(assessment.signatures?.patient?.full_name || assessment.patient_name || "");
  const [patientSig, setPatientSig] = useState(assessment.signatures?.patient?.signature_data_url || "");
  const [clinName, setClinName] = useState(assessment.signatures?.clinician?.full_name || assessment.clinician_name || "");
  const [clinSig, setClinSig] = useState(assessment.signatures?.clinician?.signature_data_url || "");
  const [relationship, setRelationship] = useState(assessment.signatures?.patient?.relationship || "self");
  const [saving, setSaving] = useState(false);

  const saveSig = async (role) => {
    setSaving(true);
    try {
      const payload = role === "patient"
        ? { role: "patient", full_name: patientName, signature_data_url: patientSig, relationship, method: "digital" }
        : { role: "clinician", full_name: clinName, signature_data_url: clinSig, method: "digital" };
      if (!payload.signature_data_url) throw new Error(t("assess.sigMissing"));
      if (!payload.full_name) throw new Error(t("assess.fullName"));
      const r = await api.post(`/assessments/${assessment.id}/signature`, payload);
      setAssessment(r.data);
      toast.success(role === "patient" ? t("assess.savePatientSig") : t("assess.saveClinSig"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="fl-card p-4">
        <div className="fl-heading text-sm mb-2">{t("assess.signPatient")}</div>
        <div className="text-[11px] mb-2" style={{ color: "var(--fl-muted-2)" }}>
          {t("assess.signPatientHint")}
        </div>
        <Field label={t("assess.fullName")}><Input value={patientName} onChange={setPatientName} locked={locked} testid="pt-sig-name" /></Field>
        <div className="mt-2">
          <Field label={t("assess.relationship")}>
            <Select value={relationship} onChange={setRelationship}
                    options={[
                      { value: "self", label: t("assess.relationshipOpts.self") },
                      { value: "parent", label: t("assess.relationshipOpts.parent") },
                      { value: "guardian", label: t("assess.relationshipOpts.guardian") },
                      { value: "representative", label: t("assess.relationshipOpts.representative") },
                    ]} locked={locked} />
          </Field>
        </div>
        <div className="mt-2">
          <SignaturePad value={patientSig} onChange={setPatientSig} disabled={locked} testid="patient-signature" />
        </div>
        {!locked && (
          <button onClick={() => saveSig("patient")} disabled={saving} className="fl-btn-ghost mt-3 text-xs px-3 py-1.5 rounded-md" data-testid="btn-save-patient-sig">
            {t("assess.savePatientSig")}
          </button>
        )}
        {assessment.signatures?.patient?.at && (
          <div className="mt-2 text-[10px]" style={{ color: "var(--fl-muted-2)" }}>
            {t("assess.signedAt")} {new Date(assessment.signatures.patient.at).toLocaleString()}
          </div>
        )}
      </div>

      <div className="fl-card p-4">
        <div className="fl-heading text-sm mb-2">{t("assess.signClinician")}</div>
        <div className="text-[11px] mb-2" style={{ color: "var(--fl-muted-2)" }}>
          <b>{t("assess.declaration")}</b> {t("assess.declarationText")}
        </div>
        <Field label={t("assess.clinName")}><Input value={clinName} onChange={setClinName} locked={locked} testid="cl-sig-name" /></Field>
        <div className="mt-2">
          <SignaturePad value={clinSig} onChange={setClinSig} disabled={locked} testid="clinician-signature" />
        </div>
        {!locked && (
          <button onClick={() => saveSig("clinician")} disabled={saving} className="fl-btn mt-3 text-xs px-3 py-1.5 rounded-md" data-testid="btn-save-clin-sig">
            {t("assess.saveClinSig")}
          </button>
        )}
        {assessment.signatures?.clinician?.at && (
          <div className="mt-2 text-[10px]" style={{ color: "var(--fl-muted-2)" }}>
            {t("assess.signedAt")} {new Date(assessment.signatures.clinician.at).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
}

/* =========================================================================
   Sign & Lock modal
   ========================================================================= */
function SignLockModal({ assessment, setAssessment, onClose }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const existingSig = assessment.signatures?.clinician?.signature_data_url || "";
  const [sig, setSig] = useState(existingSig);
  const [clinName, setClinName] = useState(assessment.signatures?.clinician?.full_name || user?.name || "");
  const [busy, setBusy] = useState(false);
  const [lock, setLock] = useState(true);
  const [ack, setAck] = useState(false);

  const submit = async () => {
    if (!sig) return toast.error(t("assess.sigMissing"));
    if (!clinName?.trim()) return toast.error("Enter your full name to sign.");
    if (!ack) return toast.error(t("assess.declarationConfirm"));
    setBusy(true);
    try {
      // 1) Save the freshly captured clinician signature (idempotent — overwrites any prior one).
      await api.post(`/assessments/${assessment.id}/signature`, {
        role: "clinician",
        full_name: clinName.trim(),
        signature_data_url: sig,
        method: "digital",
      });
      // 2) Sign & (optionally) lock the record. Server records signed_at / locked_at + auditor entry.
      const r = await api.post(`/assessments/${assessment.id}/sign`, { lock, declaration_confirmed: true });
      setAssessment(r.data);
      toast.success(lock ? t("assess.locked") : t("assess.signedOk"));
      // Auto-close the confirmation window after a successful confirm.
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="fl-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="sign-lock-modal">
        <div className="flex items-center gap-2 mb-2">
          <FileSignature size={18} className="text-[color:var(--fl-orange)]" />
          <div className="fl-heading text-lg">{t("assess.signModalTitle")}</div>
        </div>
        <div className="text-xs" style={{ color: "var(--fl-muted)" }}>
          {t("assess.signModalHint")}
        </div>

        <div className="mt-4">
          <div className="fl-label mb-1">Full name</div>
          <input
            className="fl-input"
            value={clinName}
            onChange={(e) => setClinName(e.target.value)}
            placeholder="Your legal full name"
            data-testid="sign-lock-name"
          />
        </div>

        <div className="mt-3">
          <div className="fl-label mb-1">Signature</div>
          <SignaturePad value={sig} onChange={setSig} testid="sign-lock-signature" />
          <div className="text-[11px] mt-1" style={{ color: "var(--fl-muted-2)" }}>
            Sign with finger, stylus, mouse or trackpad. Use <b>Clear</b> to redo.
          </div>
        </div>

        <div className="mt-3 text-[11px] p-3 rounded-md" style={{ background: "color-mix(in oklab, #F59E0B 6%, transparent)", border: "1px solid #F59E0B44", color: "var(--fl-muted)" }}>
          <b>{t("assess.declaration")}</b> {t("assess.declarationText")}
        </div>
        <label className="flex items-center gap-2 mt-3 text-xs cursor-pointer" data-testid="chk-declaration">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
          {t("assess.declarationConfirm")}
        </label>
        <label className="flex items-center gap-2 mt-2 text-xs cursor-pointer">
          <input type="checkbox" checked={lock} onChange={(e) => setLock(e.target.checked)} />
          {t("assess.lockAfter")}
        </label>

        <div className="flex justify-between gap-2 mt-6">
          <button
            type="button"
            onClick={() => setSig("")}
            disabled={busy || !sig}
            className="fl-btn-ghost px-3 py-2 rounded-md text-xs disabled:opacity-40"
            data-testid="sign-lock-clear"
          >
            Clear signature
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} disabled={busy} className="fl-btn-ghost px-4 py-2 rounded-md text-sm" data-testid="sign-lock-cancel">Cancel</button>
            <button onClick={submit} disabled={busy} className="fl-btn px-4 py-2 rounded-md text-sm inline-flex items-center gap-1" data-testid="btn-confirm-sign">
              <Lock size={12} /> {busy ? "…" : lock ? "Confirm and Lock" : t("assess.signBtn")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   Addendum modal
   ========================================================================= */
function AmendStartModal({ onClose, onConfirm }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [sig, setSig] = useState("");
  const submit = () => {
    if (!reason || reason.trim().length < 3) return toast.error("Enter a reason for the amendment");
    if (!sig) return toast.error("Clinician signature required");
    onConfirm({ reason: reason.trim(), signature: sig });
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="fl-card p-6 max-w-lg w-full" data-testid="amend-start-modal">
        <div className="fl-heading text-lg mb-2">Amend signed record</div>
        <div className="text-xs mb-3" style={{ color: "var(--fl-muted)" }}>
          A signed note cannot be edited directly. Enter the reason and sign — the current version is preserved and a new amended version is created. Only clinical findings can be changed.
        </div>
        <div className="space-y-3">
          <Field label="Reason for amendment" required testid="field-amend-reason">
            <Textarea value={reason} onChange={setReason} rows={3} testid="amend-reason" />
          </Field>
          <Field label="Clinician signature" required>
            <SignaturePad value={sig} onChange={setSig} testid="amend-signature" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="fl-btn-ghost px-4 py-2 rounded-md text-sm" data-testid="amend-cancel">{t("common.cancel", "Cancel")}</button>
          <button onClick={submit} className="fl-btn px-4 py-2 rounded-md text-sm" data-testid="btn-begin-amend">Begin amendment</button>
        </div>
      </div>
    </div>
  );
}

function VersionHistoryPanel({ assessment }) {
  const [versions, setVersions] = useState([]);
  useEffect(() => {
    api.get(`/assessments/${assessment.id}/versions`).then((r) => setVersions(r.data)).catch(() => setVersions([]));
  }, [assessment.id, assessment.updated_at]);
  const amendments = assessment.amendments || [];
  if (versions.length === 0 && amendments.length === 0) {
    return (
      <div className="fl-card p-4 mb-4" data-testid="version-history-panel">
        <div className="fl-heading text-sm mb-1 flex items-center gap-2"><History size={14} /> Version history</div>
        <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>No prior versions — this record has not been amended.</div>
      </div>
    );
  }
  return (
    <div className="fl-card p-4 mb-4" data-testid="version-history-panel">
      <div className="fl-heading text-sm mb-2 flex items-center gap-2"><History size={14} /> Version history ({versions.length})</div>
      <ol className="space-y-2 text-xs">
        {versions.map((v) => (
          <li key={v.id} className="p-2 rounded-md" style={{ border: "1px solid var(--fl-border)" }} data-testid={`version-${v.version_num}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <b>v{v.version_num}</b>
              <span className="px-1.5 py-0.5 rounded-full" style={{ background: v.kind === "amendment" ? "#8B5CF622" : "#22C55E22", color: v.kind === "amendment" ? "#C4B5FD" : "#22C55E" }}>{v.kind === "amendment" ? "pre-amendment" : "signed"}</span>
              <span style={{ color: "var(--fl-muted-2)" }}>{new Date(v.captured_at).toLocaleString()}</span>
              <span style={{ color: "var(--fl-muted)" }}>· {v.captured_by_email} ({v.captured_by_role})</span>
            </div>
            {v.reason && <div className="mt-1"><b>Reason:</b> {v.reason}</div>}
          </li>
        ))}
      </ol>
      {amendments.length > 0 && (
        <div className="mt-3">
          <div className="fl-heading text-xs mb-1">Amendments</div>
          <div className="space-y-2">
            {amendments.map((a) => (
              <div key={a.id} className="text-xs p-2 rounded-md" style={{ border: "1px solid var(--fl-border)" }}>
                <div className="flex items-center gap-2 flex-wrap">
                  <b>{new Date(a.at).toLocaleString()}</b>
                  <span style={{ color: "var(--fl-muted-2)" }}>{a.signed_by_name} · {a.signed_by_role}</span>
                </div>
                <div><b>Reason:</b> {a.reason}</div>
                {a.fields_changed?.length > 0 && <div style={{ color: "var(--fl-muted-2)" }}>Fields: {a.fields_changed.join(", ")}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================================================
   Audit panel
   ========================================================================= */
function AuditPanel({ assessment }) {
  const { t } = useTranslation();
  const [audit, setAudit] = useState(assessment.audit || []);
  useEffect(() => {
    api.get(`/assessments/${assessment.id}/audit`).then((r) => setAudit(r.data)).catch(() => {});
  }, [assessment.id, assessment.updated_at]);
  return (
    <div className="fl-card p-4 mb-4" data-testid="audit-panel">
      <div className="fl-heading text-sm mb-2 flex items-center gap-2"><History size={14} /> {t("assess.auditTitle")}</div>
      {audit.length === 0 && <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>{t("assess.noAudit")}</div>}
      <ol className="space-y-1 text-xs">
        {audit.map((e, i) => (
          <li key={i} className="flex items-start gap-2">
            <span style={{ color: "var(--fl-muted-2)" }}>{new Date(e.at).toLocaleString()}</span>
            <b>{e.action}</b>
            <span style={{ color: "var(--fl-muted)" }}>· {e.actor_name} ({e.actor_role})</span>
            {e.details?.fields && <span style={{ color: "var(--fl-muted-2)" }}>· {e.details.fields.join(", ")}</span>}
          </li>
        ))}
      </ol>
      {(assessment.addenda || []).length > 0 && (
        <div className="mt-4">
          <div className="fl-heading text-sm mb-2">{t("assess.addendaTitle")}</div>
          <div className="space-y-2">
            {assessment.addenda.map((a) => (
              <div key={a.id} className="text-xs p-3 rounded-md" style={{ border: "1px solid var(--fl-border)" }}>
                <div className="flex items-center gap-2 mb-1">
                  <b>{new Date(a.at).toLocaleString()}</b>
                  <span style={{ color: "var(--fl-muted-2)" }}>{a.signed_by_name} · {a.signed_by_role}</span>
                </div>
                <div><b>{t("assess.reason")}:</b> {a.reason}</div>
                {a.original_text && <div style={{ color: "var(--fl-muted-2)" }} className="line-through"><b>{t("assess.originalText")}:</b> {a.original_text}</div>}
                <div><b>{t("assess.correctedText")}:</b> {a.corrected_text}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


/* =========================================================================
   Strength / Functional test library picker
   ========================================================================= */
function TestLibraryPicker({ kind, onAdd, locked }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [tests, setTests] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open || tests.length) return;
    const path = kind === "strength" ? "/assessments/refs/strength-tests" : "/assessments/refs/functional-tests";
    api.get(path).then((r) => setTests(r.data.tests || [])).catch(() => {});
  }, [open, kind, tests.length]);

  const pick = (t2) => {
    const row = kind === "strength"
      ? { id: crypto.randomUUID(), muscle: t2.muscle, side: "R", method: t2.method,
          position: (t2.positions || [])[0] || "", value: "", unit: t2.unit || "",
          pain: "", previous: "", lsi: "", target: t2.target || "", notes: "" }
      : { id: crypto.randomUUID(), test: t2.test, protocol: t2.protocol, side: "",
          value: "", unit: t2.unit || "", pain: "", quality: "", previous: "",
          target: t2.target || "", pass_fail: "", notes: "" };
    onAdd(row);
    toast.success(t("assess.added"));
  };

  const filtered = tests.filter((tt) =>
    !q || (kind === "strength" ? tt.muscle : tt.test)?.toLowerCase().includes(q.toLowerCase()) ||
    tt.method?.toLowerCase().includes(q.toLowerCase()) || tt.protocol?.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="mb-3">
      <button
        type="button"
        disabled={locked}
        onClick={() => setOpen(true)}
        className="fl-btn-ghost text-xs px-3 py-1.5 rounded-md inline-flex items-center gap-1 disabled:opacity-50"
        data-testid={`${kind}-library-open`}
      >
        <BookOpen size={12} /> {t("assess.pickFromLibrary")}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="fl-card p-5 max-w-2xl w-full max-h-[80vh] flex flex-col" data-testid={`${kind}-library-modal`}>
            <div className="flex items-center justify-between mb-3">
              <div className="fl-heading text-lg">{kind === "strength" ? t("assess.strengthLibrary") : t("assess.functionalLibrary")}</div>
              <button onClick={() => setOpen(false)} className="text-xs" style={{ color: "var(--fl-muted)" }}>×</button>
            </div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={kind === "strength" ? t("assess.searchLibStrength") : t("assess.searchLibFunctional")}
              className="fl-input text-sm px-3 py-2 rounded-md mb-3"
              data-testid={`${kind}-library-search`}
            />
            <div className="overflow-y-auto space-y-2 flex-1">
              {filtered.map((tt) => (
                <button
                  key={tt.id}
                  type="button"
                  onClick={() => pick(tt)}
                  className="fl-card p-3 w-full text-left hover:border-[color:var(--fl-orange)] transition-all"
                  data-testid={`${kind}-lib-${tt.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="fl-heading text-sm">{kind === "strength" ? tt.muscle : tt.test}</div>
                      <div className="text-xs" style={{ color: "var(--fl-muted)" }}>
                        {tt.method || tt.protocol}
                        {tt.unit ? ` · ${tt.unit}` : ""}
                        {tt.lsi ? ` · ${t("assess.lsiLabel")}` : ""}
                      </div>
                      {tt.target && <div className="text-[10px] mt-0.5" style={{ color: "var(--fl-muted-2)" }}>{t("assess.targetPrefix")} {tt.target}</div>}
                    </div>
                    <Plus size={16} className="text-[color:var(--fl-orange)] flex-shrink-0" />
                  </div>
                </button>
              ))}
              {filtered.length === 0 && <div className="text-xs text-center py-6" style={{ color: "var(--fl-muted-2)" }}>{t("assess.noMatches")}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================================================
   Reassessment / discharge time-series charts
   ========================================================================= */
function ReassessmentCharts({ patientId }) {
  const { t } = useTranslation();
  const [timeline, setTimeline] = useState(null);
  const [tab, setTab] = useState("rom");

  useEffect(() => {
    api.get(`/assessments/timeline/patient/${patientId}`).then((r) => setTimeline(r.data)).catch(() => setTimeline({ rom: [], strength: [], functional: [], outcome_measures: [], assessment_count: 0 }));
  }, [patientId]);

  if (!timeline) return <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>{t("assess.loadingTimeline")}</div>;
  if (timeline.assessment_count < 1) {
    return (
      <div className="text-sm p-6 text-center rounded-md" style={{ color: "var(--fl-muted-2)", border: "1px dashed var(--fl-border)" }}>
        {t("assess.noPreviousData")}
      </div>
    );
  }

  const tabs = [
    { id: "rom", label: `${t("assess.sections.rom")} (${timeline.rom.length})` },
    { id: "strength", label: `${t("assess.sections.strength")} (${timeline.strength.length})` },
    { id: "functional", label: `${t("assess.sections.functional")} (${timeline.functional.length})` },
    { id: "outcomes", label: `${t("assess.sections.outcomes")} (${timeline.outcome_measures.length})` },
  ];

  return (
    <div className="space-y-3">
      <div className="flex gap-1 flex-wrap">
        {tabs.map((tt) => (
          <button
            key={tt.id}
            onClick={() => setTab(tt.id)}
            className="px-3 py-1 text-xs rounded-full border transition-colors"
            style={{
              background: tab === tt.id ? "var(--fl-orange)" : "transparent",
              color: tab === tt.id ? "#111" : "var(--fl-muted)",
              borderColor: tab === tt.id ? "var(--fl-orange)" : "var(--fl-border)",
            }}
            data-testid={`reass-tab-${tt.id}`}
          >
            {tt.label}
          </button>
        ))}
      </div>

      <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>
        {t(timeline.assessment_count === 1 ? "assess.basedOn_one" : "assess.basedOn_other", { count: timeline.assessment_count })}
      </div>

      {tab === "rom" && <ChartGroup series={timeline.rom} groupBy={(x) => `${x.movement} (${x.side})`} valueKey="arom" typicalKey="typical" label="AROM (°)" />}
      {tab === "strength" && <ChartGroup series={timeline.strength} groupBy={(x) => `${x.muscle} (${x.side})`} valueKey="value" label="Value" />}
      {tab === "functional" && <ChartGroup series={timeline.functional} groupBy={(x) => `${x.test}${x.side ? ` (${x.side})` : ""}`} valueKey="value" label="Value" />}
      {tab === "outcomes" && <ChartGroup series={timeline.outcome_measures} groupBy={(x) => `${x.name} (${x.scale || ""})`} valueKey="score" label="Score" />}
    </div>
  );
}

function ChartGroup({ series, groupBy, valueKey, typicalKey, label }) {
  const { t } = useTranslation();
  // Group by name+side and produce {date, value, typical}
  const groups = useMemo(() => {
    const map = new Map();
    series.forEach((s) => {
      const k = groupBy(s);
      if (!map.has(k)) map.set(k, []);
      const v = Number(s[valueKey]);
      if (!Number.isNaN(v)) map.get(k).push({ date: s.date, value: v, typical: typicalKey ? Number(s[typicalKey]) : undefined });
    });
    return [...map.entries()].map(([name, points]) => ({ name, points: points.sort((a, b) => a.date.localeCompare(b.date)) }));
  }, [series, groupBy, valueKey, typicalKey]);

  if (groups.length === 0) {
    return <div className="text-xs italic p-4 text-center" style={{ color: "var(--fl-muted-2)" }}>{t("assess.noNumericData")}</div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {groups.map((g) => {
        const first = g.points[0]?.value;
        const last = g.points[g.points.length - 1]?.value;
        const pct = (first != null && last != null && first !== 0) ? Math.round(((last - first) / first) * 100) : null;
        return (
          <div key={g.name} className="fl-card p-3" data-testid={`chart-${g.name.replace(/[^a-z0-9]+/gi, "-")}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="fl-heading text-sm">{g.name}</div>
              {pct != null && (
                <span className="text-[11px] font-semibold" style={{ color: pct >= 0 ? "#22C55E" : "#EF4444" }}>
                  {pct >= 0 ? "▲" : "▼"} {Math.abs(pct)}%
                </span>
              )}
            </div>
            <div className="text-[10px] mb-1" style={{ color: "var(--fl-muted-2)" }}>
              {t("assess.baselineLatest", { first: first ?? "—", last: last ?? "—" })}
            </div>
            <div style={{ width: "100%", height: 160 }}>
              <ResponsiveContainer>
                <LineChart data={g.points}>
                  <CartesianGrid stroke="var(--fl-border)" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "var(--fl-muted)" }} />
                  <YAxis tick={{ fontSize: 10, fill: "var(--fl-muted)" }} />
                  <Tooltip contentStyle={{ background: "var(--fl-bg-2)", border: "1px solid var(--fl-border)", fontSize: 11 }} />
                  <Line type="monotone" dataKey="value" stroke="#f47a20" strokeWidth={2} dot={{ r: 3 }} />
                  {typicalKey && <Line type="monotone" dataKey="typical" stroke="#6B7280" strokeWidth={1} strokeDasharray="4 4" dot={false} />}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      })}
    </div>
  );
}
